/** @format */

import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { Observable, catchError, of, throwError } from 'rxjs';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { Environment } from '@shared/data-access/interfaces';
import { Logger } from '@shared/data-access/services';
import { SocialContentDaoRequestKey } from '../model/social-content.dao.enum';
import { SocialContentResponse } from '../model/social-content.interface';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
	providedIn: 'root'
})
export class SocialContentDao {
	private readonly SOCIAL_CONTENT_BY_PRODUCT_STATE_KEY_PREFIX = 'social-content-';
	private readonly platformId = inject(PLATFORM_ID);
	private readonly http = inject(HttpClient);
	private readonly environment = inject(Environment);
	private readonly logger = inject(Logger);
	private readonly state = inject(TransferState);

	private getHttpHeaders(): HttpHeaders {
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});
		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${DCSG_CONSTANTS.timeouts.short}`
			);
			httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
		}

		return httpHeaders;
	}

	private getBaseHttpParams(limit: number): HttpParams {
		return new HttpParams()
			.set('noExpired', 'true')
			.set('limit', limit)
			.set('requireProduct', 'true');
	}

	/**
	 * @description
	 * Gets tagged social image content data in Reveal from BazaarVoice for a given product by ID / SKU
	 */
	public getSocialContentProductId$(
		productId: string,
		limit = 10
	): Observable<SocialContentResponse> {
		// Get unique key
		const STATE_KEY = makeStateKey<SocialContentResponse>(
			`${this.SOCIAL_CONTENT_BY_PRODUCT_STATE_KEY_PREFIX}${productId}`
		);

		// Attempt to get the state by key
		const stateData = this.state.get<SocialContentResponse>(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return of(stateData);
		}

		// Query String Parameters
		const httpParams = this.getBaseHttpParams(limit).append('filter', `productId:${productId}`);

		// Standard HTTP Headers
		const httpHeaders = this.getHttpHeaders();

		const url = `${this.environment.thirdParty.bv.socialApi.proxyConfig.proxy.path}${this.environment.thirdParty.bv.socialApi.dataSourceIdReveal}`;

		// Make the HTTP Request
		return this.http
			.get<SocialContentResponse>(url, {
				headers: httpHeaders,
				params: httpParams
			})
			.pipe(
				catchError((error: HttpErrorResponse) => {
					this.logger.dynamicallyLog(
						SocialContentDaoRequestKey.BYPRODUCTID,
						productId,
						error,
						url
					);
					return throwError(() => error);
				})
			);
	}

	/**
	 * @description
	 * Gets tagged social image content data in Reveal from BazaarVoice for a given product by Product URL (https://www.dickssportinggoods.com/p/calia-womens-lustralux-brami-22jlowcllstrlxbrmapu/22jlowcllstrlxbrmapu)
	 */
	public getSocialContentProductUrl$(
		productUrl: string,
		limit = 10
	): Observable<SocialContentResponse> {
		// Get unique key
		const STATE_KEY = makeStateKey<SocialContentResponse>(
			`${this.SOCIAL_CONTENT_BY_PRODUCT_STATE_KEY_PREFIX}${productUrl}`
		);

		// Attempt to get the state by key
		const stateData = this.state.get<SocialContentResponse>(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return of(stateData);
		}

		// Query String Parameters
		const httpParams = this.getBaseHttpParams(limit).append(
			'filter',
			`productUrl:${productUrl}`
		);

		// Standard HTTP Headers
		const httpHeaders = this.getHttpHeaders();

		const url = `${this.environment.thirdParty.bv.socialApi.proxyConfig.proxy.path}${this.environment.thirdParty.bv.socialApi.dataSourceIdReveal}`;

		// Make the HTTP Request
		return this.http
			.get<SocialContentResponse>(url, {
				headers: httpHeaders,
				params: httpParams
			})
			.pipe(
				catchError((error: HttpErrorResponse) => {
					this.logger.dynamicallyLog(
						SocialContentDaoRequestKey.BYPRODUCTURL,
						productUrl,
						error,
						url
					);
					return throwError(() => error);
				})
			);
	}
}
